import { useEffect, useState } from "react";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { PrimeIcons } from "primereact/api";
import { Skeleton } from "primereact/skeleton";
import { Image } from "primereact/image";

import { useAppDispatch, useAppSelector } from "store/store";

import {
  labelUploadeBy,
  labelDi,
  labelElement,
  labelElementsViewed,
  labelLastUpdate,
  labelNome,
  labelSourcesEmpty,
  labelTooltipPdf,
  labelTopic,
  labelNoResults,
  labelState,
} from "_constants";
import {
  EmptyMessageTable,
  FileDetailRow,
  IconSortTable,
  StateSource,
} from "components/shared";
import {
  resetFiltersSources,
  setCurrentSource,
  setIsLoadingSources,
  setPageSourcesAndSize,
  setSortingSources,
  setSources,
  updateSourceStatusById,
} from "store/slicer/GestioneSourcesSlice";
import { SourceTableRowButtonActions } from "./SourceTableRowButtonActions";
import { Tooltip } from "primereact/tooltip";
import { getSources, retryProcessingSource } from "services/SourceService";
import { formatDataSource, getFileIconByFileType, handleSort } from "utils";
import { useDetailPanelProvider } from "providers/DetailPanelProvider";
import { SourceDetailPanelHeader } from "./detailPanel/SourceDetailPanelHeader";
import { SourceDetailPanelBodyTopicContainer } from "./detailPanel/SourceDetailPanelBodyTopicContainer";
import { Source } from "interfaces";

export const SourcesTable = () => {
  const dispatch = useAppDispatch();
  const [totalCount, setTotalCount] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const { showPanel } = useDetailPanelProvider();

  const filters = useAppSelector((state) => state.gestioneSourcesSlice.filters);
  const isLoadingSources = useAppSelector(
    (state) => state.gestioneSourcesSlice.isLoadingSources
  );
  const sourcesList = useAppSelector(
    (state) => state.gestioneSourcesSlice.sourcesList
  );
  const sortedDirection = useAppSelector(
    (state) => state.gestioneSourcesSlice.sortedDirection
  );
  const sortedColumn = useAppSelector(
    (state) => state.gestioneSourcesSlice.sortedColumn
  );

  useEffect(() => {
    return () => {
      dispatch(resetFiltersSources());
    };
  }, [dispatch]);

  useEffect(() => {
    if (filters.pageIndex > -1) {
      dispatch(setIsLoadingSources(true));
      getSources(filters)
        .then((response) => {
          setTotalCount(response.data.totalCount);
          dispatch(setSources(response.data.items));
        })
        .catch((_) => {})
        .finally(() =>
          setTimeout(() => {
            dispatch(setIsLoadingSources(false));
            setFirstLoad(false);
          }, 500)
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onPage = (event: DataTableStateEvent) => {
    if (event.page || event.rows) {
      dispatch(
        setPageSourcesAndSize({
          page: event.page ? event.page + 1 : 1,
          size: event.rows,
        })
      );
    }
  };

  const callHandleSort = (field: String) => {
    handleSort(
      sortedColumn,
      sortedDirection,
      dispatch,
      setSortingSources,
      field
    );
  };

  const template2 = {
    layout:
      "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
    RowsPerPageDropdown: (options: any) => {
      const dropdownOptions = [
        { label: 5, value: 5 },
        { label: 10, value: 10 },
        { label: 25, value: 25 },
      ];

      return (
        <Dropdown
          value={options.value}
          options={dropdownOptions}
          onChange={options.onChange}
        />
      );
    },

    CurrentPageReport: (options: any) => {
      const first = (filters.pageIndex - 1) * filters.pageSize + 1;
      const last =
        (filters.pageIndex - 1) * filters.pageSize + filters.pageSize;
      return (
        <span className="xs-none">
          {labelElementsViewed}
          {first} - {last} {labelDi}
          {totalCount} {labelElement}
          {options.totalRecords > 1 ? "i" : "o"}
        </span>
      );
    },
  };

  const showDetailPanel = (rowData: any) => {
    dispatch(setCurrentSource(rowData));
    showPanel({
      icons: <SourceTableRowButtonActions />,
      header: <SourceDetailPanelHeader />,
      body: (
        <div>
          <SourceDetailPanelBodyTopicContainer />
          <FileDetailRow />
        </div>
      ),
    });
  };

  const getEmptyMessageTableLabel = () => {
    return isLoadingSources
      ? ""
      : filters.pageIndex === 1 &&
        !filters.globalSearch?.length &&
        !filters.topicIdList &&
        !filters.sorting
      ? labelSourcesEmpty
      : labelNoResults;
  };

  const handleErrorClick = (source: Source) => {
    retryProcessingSource({ sourceId: source.id })
      .then((_) => {
        dispatch(updateSourceStatusById(source.id));
      })
      .catch((_) => {});
  };

  return (
    <DataTable
      value={sourcesList}
      size="small"
      lazy
      dataKey="id"
      paginator={sourcesList?.length ? true : false}
      rows={filters.pageSize}
      totalRecords={totalCount}
      scrollable
      scrollHeight={`calc(100vh - ${sourcesList?.length ? "240" : "180"}px)`}
      onPage={onPage}
      first={filters.pageIndex * filters.pageSize - 1}
      paginatorClassName="flex-container justify-end"
      className={`margin-18 custom-table-source ${
        sourcesList.length ? "has-element" : ""
      }`}
      tableStyle={{ minWidth: sourcesList?.length ? "1332px" : "50vw" }}
      emptyMessage={
        !firstLoad ? (
          <EmptyMessageTable label={getEmptyMessageTableLabel()} />
        ) : (
          <div></div>
        )
      }
      paginatorTemplate={template2}
    >
      <Column
        field="name"
        header={
          <IconSortTable
            nomeCampo={labelNome}
            onSortCallback={() =>
              isLoadingSources ? null : callHandleSort("name")
            }
            iconStatus={
              sortedColumn !== "name" ? null : sortedDirection === "ASC" ? 1 : 2
            }
          />
        }
        body={
          isLoadingSources ? (
            <div className="table-row-skeleton">
              <Skeleton />
            </div>
          ) : (
            (rowData) => (
              <div className="flex-container gap-14 align-center custom-body-table">
                <Image
                  src={getFileIconByFileType(rowData.fileType)}
                  alt={labelTooltipPdf}
                />
                <Tooltip
                  target={`.source-name`}
                  mouseTrack
                  mouseTrackLeft={10}
                />
                <div
                  className="source-name ellipsis"
                  data-pr-tooltip={rowData.name}
                >
                  {rowData.name}
                </div>
              </div>
            )
          )
        }
      />
      <Column
        field="topic"
        header={
          <IconSortTable
            nomeCampo={labelTopic}
            onSortCallback={() =>
              isLoadingSources ? null : callHandleSort("topic")
            }
            iconStatus={
              sortedColumn !== "topic"
                ? null
                : sortedDirection === "ASC"
                ? 1
                : 2
            }
          />
        }
        body={
          isLoadingSources ? (
            <div className="table-row-skeleton">
              <Skeleton />
            </div>
          ) : (
            (rowData) => (
              <>
                <Tooltip
                  target={`.topic-name`}
                  mouseTrack
                  mouseTrackLeft={10}
                />

                <div
                  className={`topic-name topic-chip`}
                  data-pr-tooltip={rowData.topic}
                >
                  <span className="ellipsis">{rowData.topic}</span>
                </div>
              </>
            )
          )
        }
      />
      <Column
        field="createdBy"
        header={
          <IconSortTable
            nomeCampo={labelUploadeBy}
            onSortCallback={() =>
              isLoadingSources ? null : callHandleSort("createdBy")
            }
            iconStatus={
              sortedColumn !== "createdBy"
                ? null
                : sortedDirection === "ASC"
                ? 1
                : 2
            }
          />
        }
        body={
          isLoadingSources ? (
            <div className="table-row-skeleton">
              <Skeleton />
            </div>
          ) : (
            (rowData) => (
              <>
                <Tooltip
                  target={`${
                    rowData.createdBy?.length > 25
                      ? ".created-by-name-source"
                      : ""
                  }`}
                  mouseTrack
                  mouseTrackLeft={10}
                />

                <div
                  className={`created-by-name-source ellipsis fit-content`}
                  data-pr-tooltip={rowData.createdBy}
                >
                  {rowData.createdBy}
                </div>
              </>
            )
          )
        }
      />
      <Column
        field="lastUpdate"
        header={
          <IconSortTable
            nomeCampo={labelLastUpdate}
            onSortCallback={() =>
              isLoadingSources ? null : callHandleSort("lastUpdate")
            }
            iconStatus={
              sortedColumn !== "lastUpdate"
                ? null
                : sortedDirection === "ASC"
                ? 1
                : 2
            }
          />
        }
        body={
          isLoadingSources ? (
            <div className="table-row-skeleton">
              <Skeleton />
            </div>
          ) : (
            (rowData) => (
              <div className="flex-container justify-between align-center custom-body-table last">
                <div>{formatDataSource(rowData.lastUpdate)}</div>
              </div>
            )
          )
        }
      />
      <Column
        field="aiStatus"
        header={
          <IconSortTable
            nomeCampo={labelState}
            onSortCallback={() =>
              isLoadingSources ? null : callHandleSort("aiStatus")
            }
            iconStatus={
              sortedColumn !== "aiStatus"
                ? null
                : sortedDirection === "ASC"
                ? 1
                : 2
            }
          />
        }
        body={
          isLoadingSources ? (
            <div className="table-row-skeleton">
              <Skeleton />
            </div>
          ) : (
            (rowData) => (
              <div className="flex-container justify-between align-center custom-body-table last">
                <StateSource
                  type={rowData.aiStatus}
                  onErrorClick={() => handleErrorClick(rowData)}
                />
                <div className="flex-container gap-8">
                  <Button
                    icon={PrimeIcons.EYE}
                    text
                    className="width-px-28 height-px-28"
                    severity="secondary"
                    onClick={() => showDetailPanel(rowData)}
                  />
                  <div className="divider-action-table"></div>
                  <SourceTableRowButtonActions source={rowData} />
                </div>
              </div>
            )
          )
        }
      />
    </DataTable>
  );
};
